import useSignAnalytics from '@bridebook/web/app-basic/lib/hooks/use-sign-analytics';
import { FunctionComponent, memo, ReactNode } from 'react';
import { cn } from '@bridebook/aura';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { TLoggedOutPageClickedSection } from '@bridebook/web/app-shared/lib/types';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { createReverseMappingForEnum } from '@bridebook/web/lib/utils';
import {
  isLoggedOutToolPage,
  isSupplierPage,
  LoggedOutToolPaths,
  usePathWithoutMarket,
} from '@bridebook/web/lib/utils/url';

export enum BasicSignUpLinkMode {
  DesktopOnly = 'desktopOnly',
  Inline = 'inline',
}

const modeClasses: Record<BasicSignUpLinkMode, string> = {
  [BasicSignUpLinkMode.DesktopOnly]: 'max-lg:hidden',
  [BasicSignUpLinkMode.Inline]: 'inline-flex',
};

export interface BasicSignUpLinkParams {
  mode?: BasicSignUpLinkMode;
  showLogin?: boolean;
  children?: ReactNode;
  clickedLocation?: string;
  clickedSection?: TLoggedOutPageClickedSection;
}

const BasicSignUpLink: FunctionComponent<BasicSignUpLinkParams> = ({
  showLogin,
  children,
  mode,
  clickedLocation,
  clickedSection,
}) => {
  const linkUrl = showLogin ? UrlHelper.login : UrlHelper.signup;
  const pathname = usePathWithoutMarket();

  const parseClickLocation = () => {
    if (isSupplierPage(pathname)) return 'supplierProfile';
    if (isSearchResultsPage(pathname)) return 'search';
    if (isLoggedOutToolPage(pathname))
      return createReverseMappingForEnum(LoggedOutToolPaths)[pathname];
    return pathname;
  };

  const analyticsHandler = useSignAnalytics(
    clickedLocation ? clickedLocation : parseClickLocation(),
    clickedSection ? clickedSection : 'header',
    'Sign up',
  );

  return (
    <Link
      href={linkUrl}
      rel="nofollow"
      // Analytics is a fire-and-forget dispatch, so attach it straight to the anchor
      onClick={clickedLocation ? analyticsHandler : undefined}
      className={cn('block no-underline', mode && modeClasses[mode])}>
      {children}
    </Link>
  );
};

export default memo(BasicSignUpLink);
